import http from "@/helper/http";

const state = {
  // Current group context
  currentGroup: null,
  currentGroupPermissions: [],
  currentUserRole: null,
  isCurrentUserOwner: false,

  // Loading states
  loading: false,
  loadingPermissions: false,
};

const getters = {
  // Current group context
  currentGroup: (state) => state.currentGroup,
  currentGroupPermissions: (state) => state.currentGroupPermissions,
  currentUserRole: (state) => state.currentUserRole,
  isCurrentUserOwner: (state) => state.isCurrentUserOwner,

  // Permission checks
  isGroupOwner: (state) => state.isCurrentUserOwner,
  canManageGroup: (state) => {
    const permissions = state.currentGroupPermissions;
    return (
      permissions.includes("CAN_MANAGE_GROUP") ||
      state.isCurrentUserOwner
    );
  },
  canManagePromoCodes: (state) => {
    const permissions = state.currentGroupPermissions;
    return (
      permissions.includes("CAN_MANAGE_PROMO_CODES") ||
      state.isCurrentUserOwner
    );
  },
  canManageSettings: (state) => {
    const permissions = state.currentGroupPermissions;
    return (
      permissions.includes("CAN_MANAGE_SETTINGS") ||
      state.isCurrentUserOwner
    );
  },
  canReadPromoCodes: (state) => {
    const permissions = state.currentGroupPermissions;
    return (
      permissions.includes("CAN_READ_PROMO_CODES") ||
      state.isCurrentUserOwner
    );
  },
  canManageStores: (state) => {
    const permissions = state.currentGroupPermissions;
    return (
      permissions.includes("CAN_MANAGE_STORES") ||
      state.isCurrentUserOwner
    );
  },
  canManageInvites: (state) => {
    const permissions = state.currentGroupPermissions;
    return (
      permissions.includes("CAN_MANAGE_INVITES") ||
      state.isCurrentUserOwner
    );
  },
  canTransferInventory: (state) => {
    const permissions = state.currentGroupPermissions;
    return (
      permissions.includes("CAN_TRANSFER_INVENTORY") ||
      state.isCurrentUserOwner
    );
  },

  // Loading states
  loading: (state) => state.loading,
  loadingPermissions: (state) => state.loadingPermissions,
};

const mutations = {
  SET_CURRENT_GROUP(state, group) {
    state.currentGroup = group;
  },
  SET_CURRENT_GROUP_PERMISSIONS(state, permissions) {
    state.currentGroupPermissions = permissions;
  },
  SET_CURRENT_USER_ROLE(state, role) {
    state.currentUserRole = role;
  },
  SET_IS_CURRENT_USER_OWNER(state, isOwner) {
    state.isCurrentUserOwner = isOwner;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_LOADING_PERMISSIONS(state, loading) {
    state.loadingPermissions = loading;
  },
};

const actions = {
  async leaveGroup(_context, { groupId, storeId }) {
    const response = await http.post(`/v2/groups/${groupId}/leave`, {
      store_id: storeId,
    });
    return response.data;
  },

  async setCurrentGroup({ commit }, { group, userId }) {
    commit("SET_CURRENT_GROUP", group);
    commit("SET_LOADING_PERMISSIONS", true);

    try {
      // Fetch user permissions for this group
      const response = await http.get(
        `/v2/groups/${group.id}/users/${userId}/permissions`
      );
      const permissions = response.data?.permissions || [];
      const role = response.data?.role || null;
      const isOwner = Boolean(response.data?.is_owner);

      commit("SET_CURRENT_GROUP_PERMISSIONS", permissions);
      commit("SET_CURRENT_USER_ROLE", role || (isOwner ? "OWNER" : null));
      commit("SET_IS_CURRENT_USER_OWNER", isOwner);
    } catch (error) {
      console.error("Failed to load group permissions:", error);
      // Default to no permissions if API fails
      commit("SET_CURRENT_GROUP_PERMISSIONS", []);
      commit("SET_CURRENT_USER_ROLE", null);
      commit("SET_IS_CURRENT_USER_OWNER", false);
    } finally {
      commit("SET_LOADING_PERMISSIONS", false);
    }
  },

  clearCurrentGroup({ commit }) {
    commit("SET_CURRENT_GROUP", null);
    commit("SET_CURRENT_GROUP_PERMISSIONS", []);
    commit("SET_CURRENT_USER_ROLE", null);
    commit("SET_IS_CURRENT_USER_OWNER", false);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
